import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLeadForm } from "../../hooks/useLeadForm";


export default function LeadForm({
  prefill,
  lockPrefilled,
  mailserverApi,
  context,
  titleKey = "landing.form_title",
  subtitleKey = "landing.form_subtitle",
  allowAttachments = true,
  className = "",
  initialMessage = "",
  onSuccess,
  conversion,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const {
    form, setForm,
    files, onPick, removeFile,
    submitting, done, error, apiResult,
    canSubmit,
    submit,
    validation,
  } = useLeadForm({
    mailserverApi,
    context,
    conversion,
    allowAttachments,
    initialForm: { name: "", phone: "", email: "", message: initialMessage || "", website: "" },
  });

    // ✅ Prefill (npr. ulogovan user) -> upiši u form, ali samo ako su polja prazna
  useEffect(() => {
    if (!prefill) return;

    setForm((f) => ({
      ...f,
      name: f.name?.trim() ? f.name : (prefill.name || ""),
      email: f.email?.trim() ? f.email : (prefill.email || ""),
      phone: f.phone?.trim() ? f.phone : (prefill.phone || ""),
      // message NE diramo (da ne pregazimo initialMessage i ono što korisnik kuca)
    }));
  }, [prefill?.name, prefill?.email, prefill?.phone, setForm]);

  // ✅ pozovi onSuccess tek kad state stvarno postane "done"
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (!done) {
      notifiedRef.current = false; // reset kad se forma resetuje/remountuje
      return;
    }
    if (notifiedRef.current) return;

    notifiedRef.current = true;
    onSuccess?.({ done, apiResult, error });
  }, [done, apiResult, error, onSuccess]);
  

  function handleOpenPicker() {
    fileInputRef.current?.click();
  }

  return (
    <form
      onSubmit={async (e) => {
        await submit(e);
        // ❌ ukloni onSuccess odavde (state je još "stari")
      }}
      className={`
        rounded-3xl border p-6 md:p-8 shadow-lg
        bg-background-light dark:bg-background-dark
        border-slate-200 dark:border-neutral-800
        ${className}
      `}
    >
      <h2 className="text-2xl font-extrabold text-text-light dark:text-text-dark">
        {t(titleKey)}
      </h2>
      <p className="mt-2 text-text-light/80 dark:text-text-dark/80">
        {t(subtitleKey)}
      </p>

      {/* Name */}
      <label className="text-sm font-medium text-text-light dark:text-text-dark">
        {t("landing.form_name_label")}
      </label>
      <input
        type="text"
        maxLength={200}
        className={`mt-1 w-full rounded-xl border px-3 py-2 
          bg-background-light dark:bg-neutral-900 
          text-text-light dark:text-text-dark 
          placeholder:text-slate-400 dark:placeholder:text-neutral-500 
          focus:outline-none focus:ring-2 
          focus:ring-primary-light dark:focus:ring-primary-dark
          ${validation.nameOk ? "border-slate-300 dark:border-neutral-700" : "border-red-300 dark:border-primary-dark"}`}
        placeholder={t("landing.form_name_ph")}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
      />

      {/* Phone */}
      <label className="mt-3 block text-sm font-medium text-text-light dark:text-text-dark">
        {t("landing.form_phone_label")}
      </label>
      <input
        type="tel"
        maxLength={50}
        className={`mt-1 w-full rounded-xl border px-3 py-2 
          bg-background-light dark:bg-neutral-900 
          text-text-light dark:text-text-dark 
          placeholder:text-slate-400 dark:placeholder:text-neutral-500 
          focus:outline-none focus:ring-2 
          focus:ring-primary-light dark:focus:ring-primary-dark
          ${validation.phoneOk ? "border-slate-300 dark:border-neutral-700" : "border-red-300 dark:border-primary-dark"}`}
        placeholder={t("landing.form_phone_ph")}
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        required
      />

      {/* Email */}
      <label className="mt-3 block text-sm font-medium text-text-light dark:text-text-dark">
        {t("landing.form_email_label")}
      </label>
      <input
        type="email"
        maxLength={320}
        className={`mt-1 w-full rounded-xl border px-3 py-2 
          bg-background-light dark:bg-neutral-900 
          text-text-light dark:text-text-dark 
          placeholder:text-slate-400 dark:placeholder:text-neutral-500 
          focus:outline-none focus:ring-2 
          focus:ring-primary-light dark:focus:ring-primary-dark
          ${validation.emailOk ? "border-slate-300 dark:border-neutral-700" : "border-red-300 dark:border-primary-dark"}`}
        placeholder={t("landing.form_email_ph")}
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        required
      />

      {/* Message */}
      <label className="mt-3 block text-sm font-medium text-text-light dark:text-text-dark">
        {t("landing.form_message_label")}
      </label>
      <textarea
        maxLength={4000}
        className="mt-1 w-full h-28 rounded-xl border px-3 py-2 
          bg-background-light dark:bg-neutral-900 
          text-text-light dark:text-text-dark 
          placeholder:text-slate-400 dark:placeholder:text-neutral-500 
          focus:outline-none focus:ring-2 
          focus:ring-primary-light dark:focus:ring-primary-dark 
          border-slate-300 dark:border-neutral-700"
        placeholder={t("landing.form_message_ph")}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
      />

      {/* Attachments */}
      {allowAttachments && (
        <>
          <label className="mt-3 block text-sm font-medium text-text-light dark:text-text-dark">
            {t("landing.form_attachments_label")}
          </label>
          <div className="mt-1 flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              multiple
              onChange={onPick}
              className="sr-only"
            />

            <button
              type="button"
              onClick={handleOpenPicker}
              className="cursor-pointer inline-flex items-center justify-center 
                rounded-xl px-4 py-2 font-semibold text-white 
                bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              {t("landing.form_choose_files")}
            </button>

            {files.length > 0 ? (
              <span className="text-sm text-text-light dark:text-text-dark">
                {files.map((f) => f.name).join(", ")}
              </span>
            ) : (
              <span className="text-sm text-slate-400 dark:text-slate-500">
                {t("landing.form_no_file")}
              </span>
            )}
          </div>

          {files.length > 0 && (
            <ul className="mt-2 text-sm text-slate-600 dark:text-slate-300 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span className="truncate">
                    {f.name} ({Math.round(f.size / 1024)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-red-600 dark:text-red-400 hover:underline text-xs"
                  >
                    {t("landing.form_remove_file")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Success states */}
      {done && apiResult?.needsVerification && (
        <div className="mt-6 rounded-xl border p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200">
          <p className="font-semibold">Hvala!</p>
          <p className="text-sm mt-1">
            Poslali smo Vam email za potvrdu. Molimo proverite inbox (i spam folder).
          </p>
        </div>
      )}

      {done && apiResult && !apiResult.needsVerification && (
        <div className="mt-6 rounded-xl border p-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200">
          <p className="font-semibold">{t("landing.form_success_title")}</p>
          <p className="text-sm mt-1">{t("landing.form_success_desc")}</p>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg border p-3 text-sm bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200">
          {t("landing.form_error_generic")}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className={`
          mt-4 inline-flex justify-center items-center
          rounded-xl px-4 py-2 font-semibold text-white
          ${canSubmit
            ? "bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700"
            : "bg-slate-400/70 dark:bg-slate-600/50 cursor-not-allowed"}
        `}
      >
        {submitting ? t("landing.form_submitting") : t("landing.form_submit")}
      </button>

      <p className="mt-3 text-xs text-text-light/70 dark:text-text-dark/70">
        {t("landing.form_required_note_prefix")}
        <a href="https://modularobjects.com/cookies-policy" className="underline hover:opacity-80">
          {t("landing.form_required_note_link")}
        </a>.
      </p>
    </form>
  );
}
